import * as React from 'react';
import {
  QueryProps,
  QueryResult,
  Query /* you might need this --- OperationVariables --- */,
  OperationVariables,
  Mutation,
  MutationProps,
  MutationFn,
} from 'react-apollo';

/*
 * nested pick from https://gist.github.com/staltz/368866ea6b8a167fbdac58cddf79c1bf
 * It's essentially the `Pick` type but enables to pick fields in objects nested one level deep
 */
export type Pick2<T, K1 extends keyof T, K2 extends keyof T[K1]> = {
  [P1 in K1]: {
    [P2 in K2]: T[K1][P2];
  };
};

function GQLVariablePropConverter<TVariables, IncomingProps>(
  varName: keyof TVariables,
  propName: keyof IncomingProps,
  props: IncomingProps
) {
  return ({
    [varName]: props[propName],
    // key was returning as string despite best attempts for it not to be.
  } as unknown) as TVariables;
}

// see src/integrations/components/IntegrationSettingsIndex.tsx for usage
export function HoCQueryComponent<
  TData = any,
  TVariables = OperationVariables,
  IncomingProps = any
>(
  queryProps: Omit<QueryProps<TData, TVariables>, 'children'> & {
    variablePropKeys?: { varName: keyof TVariables; propName: keyof IncomingProps }[];
  }
) {
  return (
    WrappedComponent: React.ComponentType<
      { apolloProps: QueryResult<TData, TVariables> } & IncomingProps
    >
  ) => {
    return (props: IncomingProps) => {
      const variables = (queryProps.variablePropKeys ?? []).reduce((obj, curValue) => {
        return { ...obj, ...GQLVariablePropConverter(curValue.varName, curValue.propName, props) };
      }, {});

      return (
        <Query {...queryProps} variables={variables}>
          {(data: any) => {
            const castedData = data as QueryResult<TData, TVariables>;
            return <WrappedComponent {...{ apolloProps: castedData, ...props }} />;
          }}
        </Query>
      );
    };
  };
}

export function HoCMutationComponent<
  TData = any,
  TVariables = OperationVariables,
  IncomingProps = any
>(
  mutationProps: Omit<MutationProps<TData, TVariables>, 'optimisticResponse' | 'children'>,
  MutationFnPropName: keyof IncomingProps
) {
  return (
    WrappedComponent: React.ComponentType<
      { [x: string]: MutationFn<TData, TVariables> } & IncomingProps
    >
  ) => {
    return (props: IncomingProps) => {
      return (
        <Mutation {...mutationProps}>
          {(mutateFn: any, response: any) => {
            const castedMutateFn = (mutateFn as unknown) as MutationFn<TData, TVariables>;
            return (
              <WrappedComponent
                {...{ ...{ [MutationFnPropName]: castedMutateFn }, ...props }}></WrappedComponent>
            );
          }}
        </Mutation>
      );
    };
  };
}

// Gets only the optional types of T
export type OptionalPropertyOf<T extends object> = Exclude<
  {
    [K in keyof T]: T extends Record<K, T[K]> ? never : K;
  }[keyof T],
  undefined
>;

// Gets only the NOT optional types of T
export type NonOptionalPropertyOf<T extends object> = Exclude<
  {
    [K in keyof T]: T extends Record<K, T[K]> ? K : never;
  }[keyof T],
  undefined
>;

// Narrows the passed value to be of type T if it is possibly null or undefined
export function isNotNullOrUndefined<T>(item: T | undefined | null): item is T {
  return item !== undefined && item !== null;
}
